import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import { Share2, Home, MessageSquare, Bot, FileText, Bell, Wallet, User, Shield, LogOut, Menu, X, Sparkles, HelpCircle, Plug, CalendarDays, Images, Archive, Users } from "lucide-react";
import { Logo } from "./Logo";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { useI18n } from "../i18n";
import { IncomingCall } from "./IncomingCall";
import { FriendCallRing } from "./FriendCallRing";
import { TaskNotifier } from "./TaskChatTools";
import { UpgradePlanDialog } from "./UpgradePlanDialog";
import { NotificationBell } from "./NotificationBell";
import { registerAssetWorker } from "../lib/firebase";
import { WelcomeTour, openTour } from "./WelcomeTour";
import { PublishHost } from "./PublishDialog";
import { FilePreviewHost } from "./FilePreviewDrawer";
import { SoundToggleButton } from "./SoundToggle";
import { SupportOverlay } from "./SupportOverlay";
import { onUserEvent, useLiveSync, useLiveMode } from "../lib/userEvents";
import { PageErrorBoundary } from "./ErrorBoundary";
import { UserAvatar } from "./UserAvatar";

export function AppLayout() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [badges, setBadges] = useState({ friend_requests: 0, unread_chats: 0 });
  useEffect(() => { registerAssetWorker(); }, []);
  // WS event = trigger → GET /notifications/badges (coalesced); also on mount, reconnect and tab resume
  useLiveSync(() => { if (!user) return; api.get("/notifications/badges").then((r) => setBadges(r.data)).catch(() => {}); },
    ["message_new", "friend_request", "notification", "reminder_due", "incoming_call", "task_update", "archived", "push"], 0, [user?.id]);
  useEffect(() => {
    if (!user) return undefined;
    const pull = () => api.get("/notifications/badges").then((r) => setBadges(r.data)).catch(() => {});
    window.addEventListener("oryntix:badges", pull);
    return () => window.removeEventListener("oryntix:badges", pull);
  }, [user]);
  const badgeOf = (id) => (id === "friends" ? badges.friend_requests : id === "chat" ? badges.unread_chats : 0);

  const isAdmin = user?.role === "admin";
  const allItems = [
    { to: "/home", icon: Home, label: "Dashboard", id: "home" },
    { to: "/chat", icon: MessageSquare, label: t("nav.chat"), id: "chat" },
    { to: "/personas", icon: Bot, label: "AI Agents", id: "personas", adminOnly: true },
    { to: "/workspace", icon: FileText, label: t("nav.workspace"), id: "workspace" },
    { to: "/reminders", icon: Bell, label: t("nav.reminders"), id: "reminders" },
    { to: "/calendar", icon: CalendarDays, label: "Kalender", id: "calendar" },
    { to: "/gallery", icon: Images, label: "Galeri", id: "gallery" },
    { to: "/integrations", icon: Plug, label: "Integrasi", id: "integrations" },
    { to: "/social", icon: Share2, label: "Social Media", id: "social" },
    { to: "/archives", icon: Archive, label: "Arsip", id: "archives" },
    { to: "/friends", icon: Users, label: "Teman", id: "friends" },
    { to: "/wallet", icon: Wallet, label: t("nav.wallet"), id: "wallet", adminOnly: true },
  ];
  const { pathname } = useLocation();
  const [hasSocial, setHasSocial] = useState(() => sessionStorage.getItem("oryntix_has_social") === "1");
  useEffect(() => {
    if (!user) return;
    api.get("/social/accounts").then((r) => { const on = (r.data?.items || []).some((a) => a.connected); setHasSocial(on); sessionStorage.setItem("oryntix_has_social", on ? "1" : "0"); }).catch(() => {});
    const sync = () => setHasSocial(sessionStorage.getItem("oryntix_has_social") === "1");
    window.addEventListener("oryntix:social-changed", sync);
    return () => window.removeEventListener("oryntix:social-changed", sync);
  }, [user, pathname]);
  const items = allItems.filter((it) => (!it.adminOnly || isAdmin) && (it.id !== "social" || hasSocial));
  if (user?.platform_role && process.env.REACT_APP_PLATFORM_URL) items.push({ to: process.env.REACT_APP_PLATFORM_URL, external: true, icon: Shield, label: "Oryntix Platform", id: "platform" });
  if (user?.is_platform_admin) items.push({ to: "/admin", icon: Shield, label: t("nav.admin"), id: "admin" });

  const handleLogout = async () => { await logout(); nav("/"); };
  useEffect(() => {  // opening a page marks the notifications that point to it as read
    if (!user) return;
    const t = setTimeout(() => api.post("/notifications/read", { link: pathname }).then((r) => { if (r.data?.marked) window.dispatchEvent(new Event("oryntix:badges")); }).catch(() => {}), 400);
    return () => clearTimeout(t);
  }, [pathname, user?.id]);
  const credits = user?.trial ? (user?.usable_credits ?? user?.credits ?? 0) : (user?.credits ?? 0);  // trial: what can be spent today, not the whole trial grant
  const pct = Math.min(100, Math.round((credits / Math.max(user?.credits_cap || credits || 1, 1)) * 100));
  const [showUpgrade, setShowUpgrade] = useState(false);

  const SidebarInner = (
    <div className="flex h-full flex-col sidebar-dark text-white">
      <div className="px-5 pb-3 pt-6"><Logo light size={32} /></div>
      <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto px-3">
        {items.map((it) => it.external ? (
          <a key={it.id} href={it.to} target="_blank" rel="noreferrer" data-testid={`nav-${it.id}`} className="nav-item flex items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-medium md:text-sm">
            <it.icon size={18} className="nav-ico" /><span className="flex-1">{it.label}</span>
          </a>
        ) : (
          <NavLink key={it.id} to={it.to} data-testid={`nav-${it.id}`} onClick={() => setOpen(false)}
            className={({ isActive }) => `nav-item flex items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-medium md:text-sm ${isActive ? "active" : ""}`}>
            <it.icon size={18} className="nav-ico" />
            <span className="flex-1">{it.label}</span>
            {badgeOf(it.id) > 0 && <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#EF4444] px-1.5 text-[11px] font-bold text-white" data-testid={`badge-${it.id}`}>{badgeOf(it.id) > 99 ? "99+" : badgeOf(it.id)}</span>}
          </NavLink>
        ))}
      </nav>
      <div className="shrink-0 p-3">
        {isAdmin && (
          <div className="rounded-2xl bg-white/[0.06] p-4" data-testid="sidebar-usage">
            <p className="text-xs font-medium text-white/70">Workspace Usage</p>
            <div className="mt-3 flex items-center gap-3">
              <span className="relative flex h-12 w-12 items-center justify-center rounded-full" style={{ background: `conic-gradient(#2F6BFF ${pct}%, rgba(255,255,255,.12) 0)` }}>
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0F1A3A] text-[10px] font-bold">{pct}%</span>
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold" data-testid="sidebar-credits">{credits} <span className="text-xs font-normal text-white/60">{user?.trial ? "kredit bisa dipakai hari ini" : "kredit"}</span></p>
                <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-[#2F6BFF]" style={{ width: `${pct}%` }} /></div>
              </div>
            </div>
            <button type="button" onClick={() => { setOpen(false); setShowUpgrade(true); }} data-testid="upgrade-btn" className="mt-3 w-full rounded-lg bg-white/10 py-2 text-xs font-semibold transition hover:bg-white/15">{user?.plan === "trial" ? "Upgrade Plan" : "Tambah Kredit"}</button>
          </div>
        )}
        <div className="mt-3 flex items-center gap-2.5 rounded-xl px-2 py-2">
          <button onClick={() => nav("/profile")} className="flex min-w-0 flex-1 items-center gap-2.5 text-left" data-testid="sidebar-user">
            <UserAvatar name={user?.name || "U"} avatar={user?.avatar} size={36} testid="sidebar-user-avatar" />
            <span className="min-w-0 leading-tight">
              <span className="block truncate text-sm font-semibold">{user?.name}</span>
              <span className="block truncate text-[11px] text-white/55">{user?.is_platform_admin ? "Platform Admin" : isAdmin ? (user?.plan === "trial" ? "Workspace Owner · Trial" : "Workspace Owner") : "Member"}</span>
            </span>
          </button>
          <button onClick={handleLogout} data-testid="logout-btn" title={t("nav.logout")} className="flex h-8 w-8 items-center justify-center rounded-lg text-white/60 transition hover:bg-white/10 hover:text-white"><LogOut size={16} /></button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen mesh-bg">
      <aside className="sticky top-0 hidden h-screen w-60 shrink-0 md:block">{SidebarInner}</aside>

      {open && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-72 shadow-xl">
            <button className="absolute right-3 top-5 z-10 text-white/70" onClick={() => setOpen(false)}><X /></button>
            {SidebarInner}
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-[#E6EAF2] glass px-4 sm:px-6">
          <button className="text-slate-500 md:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu /></button>
          <div className="hidden flex-1 sm:block" aria-hidden="true" />
          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            {isAdmin && (
              <button onClick={() => nav("/wallet")} className="hidden h-10 items-center gap-1.5 rounded-full border border-[#E6EAF2] bg-white px-3 text-sm sm:flex" data-testid="topbar-credits">
                <Sparkles size={15} className="text-[#2F6BFF]" />
                <span className="font-bold text-slate-900">{credits}</span>
              </button>
            )}
            <button onClick={openTour} data-testid="help-tour-btn" className="hidden h-10 w-10 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 sm:flex" title="Bantuan — tur singkat"><HelpCircle size={19} /></button>
            <ConnectionDot />
            <SoundToggleButton />
            <NotificationBell />
            <WelcomeTour user={user} />
            <button onClick={() => nav("/profile")} className="rounded-full transition-transform hover:scale-105" data-testid="topbar-user"><UserAvatar name={user?.name || "U"} avatar={user?.avatar} size={40} /></button>
          </div>
        </header>

        <main className="min-w-0 flex-1"><PageErrorBoundary><Outlet /></PageErrorBoundary></main>
      </div>

      <IncomingCall />
      <FriendCallRing />
      <TaskNotifier />
      {showUpgrade && <UpgradePlanDialog onClose={() => setShowUpgrade(false)} />}
      <PublishHost />
      <FilePreviewHost />
      <SupportOverlay />
    </div>
  );
}


// Realtime status: green = WebSocket live · green "Push aktif" = FCM carries triggers (socket closed on purpose) · amber pulsing = reconnecting · gray = paused (tab was hidden).
function ConnectionDot() {
  const mode = useLiveMode();
  const cfg = {
    ws: ["bg-emerald-500", "Terhubung", "Realtime terhubung — pembaruan langsung via WebSocket"],
    push: ["bg-emerald-500", "Push aktif", "Hemat koneksi: notifikasi lewat push (FCM), WebSocket hanya dibuka saat dibutuhkan"],
    paused: ["bg-slate-300", "Dijeda", "Koneksi realtime dijeda saat tab tersembunyi — tersambung lagi saat tab terlihat"],
    reconnecting: ["bg-amber-400 animate-pulse", "Menyambung ulang…", "Menyambung ulang… sementara data diperbarui saat halaman dibuka"],
  }[mode] || ["bg-amber-400 animate-pulse", "Menyambung ulang…", ""];
  return (
    <span title={cfg[2]} data-testid="ws-status-dot" data-state={mode === "ws" ? "connected" : mode}
      className="flex items-center gap-1.5 rounded-full border border-[#E7ECF3] bg-white px-2 py-1 text-[11px] font-semibold text-slate-500">
      <span className={`h-2 w-2 rounded-full ${cfg[0]}`} />
      <span className="hidden sm:inline">{cfg[1]}</span>
    </span>
  );
}
